import { Badge, Group, List, Paper, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { AdminProgramme, ValidationIssue } from '@/api/types';
import { tabForField, TABS } from './model';

/**
 * The publish checklist (06 §3.1): blocking `issues` and non-blocking `warnings` from the API,
 * always visible beside the editor. Each item links to the tab that fixes it.
 */
export function ValidationChecklist({
  programme,
  dirty,
}: {
  programme: AdminProgramme;
  dirty: boolean;
}) {
  const { issues, warnings } = programme;
  return (
    <Paper withBorder p="md" radius="md" className="pb-surface" aria-labelledby="checklist-title">
      <Stack gap="sm">
        <Group justify="space-between" gap="xs">
          <Text fw={600} id="checklist-title">
            Ready to publish?
          </Text>
          {issues.length === 0 ? (
            <Badge color="leaf" leftSection={<IconCircleCheck size={12} />}>
              All checks pass
            </Badge>
          ) : (
            <Badge color="coral" leftSection={<IconAlertCircle size={12} />}>
              {issues.length} to fix
            </Badge>
          )}
        </Group>
        {dirty && (
          <Text size="xs" c="dimmed">
            Checks reflect the last saved version. Save to update them.
          </Text>
        )}
        {issues.length === 0 ? (
          <Text size="sm" c="dimmed">
            Everything needed for approval and publishing is in place.
          </Text>
        ) : (
          <IssueList items={issues} kind="issue" programmeId={programme.id} />
        )}
        {warnings.length > 0 && (
          <>
            <Text size="sm" fw={600} mt="xs">
              Recommended
            </Text>
            <IssueList items={warnings} kind="warning" programmeId={programme.id} />
          </>
        )}
      </Stack>
    </Paper>
  );
}

function IssueList({
  items,
  kind,
  programmeId,
}: {
  items: ValidationIssue[];
  kind: 'issue' | 'warning';
  programmeId: string;
}) {
  const navigate = useNavigate();
  return (
    <List
      spacing={6}
      size="sm"
      aria-label={kind === 'issue' ? 'Blocking issues' : 'Recommendations'}
      icon={
        <ThemeIcon
          size={18}
          radius="xl"
          variant="light"
          color={kind === 'issue' ? 'coral' : 'orange'}
          aria-hidden
        >
          {kind === 'issue' ? <IconAlertCircle size={12} /> : <IconInfoCircle size={12} />}
        </ThemeIcon>
      }
    >
      {items.map((i, n) => {
        const tab = tabForField(i.field);
        return (
          <List.Item key={`${i.field}-${i.code}-${n}`}>
            <UnstyledButton
              onClick={() => navigate(`/programmes/${programmeId}/${tab}`, { replace: true })}
              style={{ textAlign: 'left' }}
              aria-label={`${kind === 'issue' ? 'Fix' : 'Review'}: ${i.message} (${TABS.find((t) => t.value === tab)?.label} tab)`}
            >
              <Text size="sm" span>
                {i.message}
              </Text>{' '}
              <Text size="xs" c="dimmed" span>
                {TABS.find((t) => t.value === tab)?.label}
              </Text>
            </UnstyledButton>
          </List.Item>
        );
      })}
    </List>
  );
}
